/* eslint-disable no-unused-vars */
import * as THREE from "three";
import kmeans from "ml-kmeans";

// Basic KD-Tree implementation for nearest neighbor search
class KDTree {
  constructor(points) {
    this.points = points; // Adicionar propriedade points para acessar o array original
    this.root = this.buildTree(points, 0);
  }

  buildTree(points, depth) {
    if (points.length === 0) return null;

    const axis = depth % 3;
    points.sort((a, b) => a.getComponent(axis) - b.getComponent(axis));

    const median = Math.floor(points.length / 2);
    const node = {
      point: points[median],
      axis: axis,
      left: this.buildTree(points.slice(0, median), depth + 1),
      right: this.buildTree(points.slice(median + 1), depth + 1),
    };

    return node;
  }

  getComponent(point, axis) {
    if (axis === 0) return point.x;
    if (axis === 1) return point.y;
    if (axis === 2) return point.z;
    return 0;
  }

  nearest(queryPoint) {
    let bestNode = null;
    let bestDistance = Infinity;

    const search = (node, depth) => {
      if (node === null) return;

      const distance = queryPoint.distanceTo(node.point);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestNode = node;
      }

      const axis = node.axis;
      const difference =
        queryPoint.getComponent(axis) - node.point.getComponent(axis);

      let firstBranch = difference < 0 ? node.left : node.right;
      let secondBranch = difference < 0 ? node.right : node.left;

      search(firstBranch, depth + 1);

      if (difference * difference < bestDistance) {
        search(secondBranch, depth + 1);
      }
    };

    search(this.root, 0);
    return bestNode ? bestNode.point : null;
  }

  kNearest(queryPoint, k) {
    const nearestPoints = [];
    
    const search = (node, depth) => {
      if (node === null) {
        return;
      }

      const dist = queryPoint.distanceTo(node.point);
      nearestPoints.push({ point: node.point, distance: dist });

      const axis = node.axis;
      const difference =
        queryPoint.getComponent(axis) - node.point.getComponent(axis);

      let firstBranch = difference < 0 ? node.left : node.right;
      let secondBranch = difference < 0 ? node.right : node.left;

      search(firstBranch, depth + 1);

      // Melhorar condição para explorar segundo ramo
      if (nearestPoints.length < k || difference * difference < Math.max(...nearestPoints.map(p => p.distance))) {
        search(secondBranch, depth + 1);
      }
    };

    search(this.root, 0);
    nearestPoints.sort((a, b) => a.distance - b.distance);
    return nearestPoints.slice(0, k).map((n) => n.point);
  }
}

// Function to find the closest points between two meshes using KD-Tree
export function findClosestPointsBetweenMeshesOptimized(mesh1, mesh2) {
  let minDistance = Infinity;
  let closestPoint1 = null;
  let closestPoint2 = null;

  const positions1 = mesh1.geometry.attributes.position.array;
  const positions2 = mesh2.geometry.attributes.position.array;

  const points1 = [];
  const points2 = [];

  const tempVec = new THREE.Vector3();

  for (let i = 0; i < positions1.length; i += 3) {
    tempVec.set(positions1[i], positions1[i + 1], positions1[i + 2]);
    mesh1.localToWorld(tempVec);
    points1.push(tempVec.clone());
  }

  for (let i = 0; i < positions2.length; i += 3) {
    tempVec.set(positions2[i], positions2[i + 1], positions2[i + 2]);
    mesh2.localToWorld(tempVec);
    points2.push(tempVec.clone());
  }

  // Build KD-Trees for both sets of points
  const kdtree2 = new KDTree(points2);

  // Iterate through points of mesh1 and find nearest in mesh2
  for (const p1 of points1) {
    const nearestP2 = kdtree2.nearest(p1);
    if (nearestP2) {
      const dist = p1.distanceTo(nearestP2);
      if (dist < minDistance) {
        minDistance = dist;
        closestPoint1 = p1;
        closestPoint2 = nearestP2;
      }
    }
  }

  return { closestPoint1, closestPoint2 };
}

// Function to calculate the midline point of an arch (using optimized findClosestPointsBetweenMeshes)
export function calculateArchMidlineOptimized(incisor11Mesh, incisor21Mesh) {
  if (!incisor11Mesh || !incisor21Mesh) {
    console.warn(
      "Incisivos centrais não encontrados para calcular a linha média da arcada."
    );
    return null;
  }

  const { closestPoint1, closestPoint2 } =
    findClosestPointsBetweenMeshesOptimized(incisor11Mesh, incisor21Mesh);

  if (closestPoint1 && closestPoint2) {
    return closestPoint1.clone().add(closestPoint2).divideScalar(2);
  }
  return null;
}

// Function to calculate medial and distal points for a tooth using crown slice approach with KDTree optimization
export function calculateMedialDistalPointsOptimized(
  toothMesh,
  toothId,
  archMidlinePoint,
  previousToothMesh = null,
  nextToothMesh = null,
  sliceRange = 2.0
) {
  if (!toothMesh || !toothId) {
    return { medialPoint: null, distalPoint: null, middlePoint: null };
  }

  const geometry = toothMesh.geometry;
  const positionAttr = geometry.attributes.position;
  const vertexCount = positionAttr.count;
  
  // Identificar arcada e tipo de dente
  const isUpper = (toothId < 31);
  const lastDigit = parseInt(toothId) % 10;
  const isPosterior = lastDigit >= 5;
  
  // Ajustar range para dentes posteriores (como no getEdgeSliceCentroid)
  let adjustedRange = sliceRange;
  if (isPosterior) {
    adjustedRange *= 3;
  }

  const tempVertex = new THREE.Vector3();
  let edgeY = isUpper ? Infinity : -Infinity;

  // Encontrar a borda da coroa (incisal/oclusal)
  for (let i = 0; i < vertexCount; i++) {
    tempVertex.fromBufferAttribute(positionAttr, i);
    toothMesh.localToWorld(tempVertex);
    
    if (isUpper) {
      if (tempVertex.y < edgeY) edgeY = tempVertex.y;
    } else {
      if (tempVertex.y > edgeY) edgeY = tempVertex.y;
    }
  }

  // Definir limites da fatia da coroa
  const minY = isUpper ? edgeY : edgeY - adjustedRange;
  const maxY = isUpper ? edgeY + adjustedRange : edgeY;

  // Coletar vértices da fatia da coroa
  const crownSliceVertices = [];
  for (let i = 0; i < vertexCount; i++) {
    tempVertex.fromBufferAttribute(positionAttr, i);
    toothMesh.localToWorld(tempVertex);
    
    if (tempVertex.y >= minY && tempVertex.y <= maxY) {
      crownSliceVertices.push(tempVertex.clone());
    }
  }

  if (crownSliceVertices.length === 0) {
    return { medialPoint: null, distalPoint: null, middlePoint: null };
  }

  // Criar KDTree com os vértices da fatia da coroa para buscas otimizadas
  const kdTreeCrown = new KDTree(crownSliceVertices);

  let medialPoint = null;
  let distalPoint = null;

  // Calcular pontos medial e distal baseados na fatia da coroa usando KDTree
  if (archMidlinePoint) {
    // Usar KDTree para encontrar o ponto mais próximo (medial) da linha média
    medialPoint = kdTreeCrown.nearest(archMidlinePoint);
    
    // Para o ponto distal, encontrar o mais distante usando busca otimizada
    let maxDistance = -Infinity;
    for (const vertex of crownSliceVertices) {
      const distance = vertex.distanceTo(archMidlinePoint);
      if (distance > maxDistance) {
        maxDistance = distance;
        distalPoint = vertex.clone();
      }
    }
  } else {
    // Fallback: usar centroide da fatia como referência
    const centroid = new THREE.Vector3();
    for (const vertex of crownSliceVertices) {
      centroid.add(vertex);
    }
    centroid.divideScalar(crownSliceVertices.length);
    
    medialPoint = centroid.clone();
    distalPoint = centroid.clone();
  }

  // Calcular ponto médio entre medial e distal
  let middlePoint = null;
  if (medialPoint && distalPoint) {
    middlePoint = new THREE.Vector3()
      .addVectors(medialPoint, distalPoint)
      .divideScalar(2);
  }

  // Refinamento opcional usando dentes adjacentes se disponíveis
  if (previousToothMesh && medialPoint) {
    const refinedMedial = refinePointWithAdjacentToothKDTree(
      medialPoint, 
      toothMesh, 
      previousToothMesh, 
      kdTreeCrown
    );
    if (refinedMedial) medialPoint = refinedMedial;
  }

  if (nextToothMesh && distalPoint) {
    const refinedDistal = refinePointWithAdjacentToothKDTree(
      distalPoint, 
      toothMesh, 
      nextToothMesh, 
      kdTreeCrown
    );
    if (refinedDistal) distalPoint = refinedDistal;
  }

  // Recalcular ponto médio após refinamento
  if (medialPoint && distalPoint) {
    middlePoint = new THREE.Vector3()
      .addVectors(medialPoint, distalPoint)
      .divideScalar(2);
  }

  console.table({ medialPoint, distalPoint, middlePoint });

  return { medialPoint, distalPoint, middlePoint };
}

// Função auxiliar otimizada para refinar pontos usando dentes adjacentes com KDTree
function refinePointWithAdjacentToothKDTree(currentPoint, toothMesh, adjacentMesh, crownKDTree) {
  if (!currentPoint || !adjacentMesh || !crownKDTree) {
    return null;
  }

  // Criar KDTree para o dente adjacente
  const adjacentGeometry = adjacentMesh.geometry;
  const adjacentPositions = adjacentGeometry.attributes.position;
  const tempVertex = new THREE.Vector3();
  const adjacentVertices = [];
  
  // Coletar vértices do dente adjacente
  for (let i = 0; i < adjacentPositions.count; i++) {
    tempVertex.fromBufferAttribute(adjacentPositions, i);
    adjacentMesh.localToWorld(tempVertex);
    adjacentVertices.push(tempVertex.clone());
  }
  
  const kdTreeAdjacent = new KDTree(adjacentVertices);
  
  // Encontrar múltiplos pontos próximos para melhor precisão
  const nearestPoints = crownKDTree.kNearest(currentPoint, Math.min(10, crownKDTree.points.length));
  
  let closestDistance = Infinity;
  let refinedPoint = null;

  for (const crownVertex of nearestPoints) {
    // Usar KDTree para encontrar o ponto mais próximo no dente adjacente
    const nearestAdjacent = kdTreeAdjacent.nearest(crownVertex);
    const distance = crownVertex.distanceTo(nearestAdjacent);
    
    if (distance < closestDistance) {
      closestDistance = distance;
      refinedPoint = crownVertex.clone();
    }
  }

  return refinedPoint;
}

// Função auxiliar original mantida para compatibilidade (caso KDTree falhe)
function refinePointWithAdjacentTooth(currentPoint, toothMesh, adjacentMesh, crownVertices) {
  if (!currentPoint || !adjacentMesh || crownVertices.length === 0) {
    return null;
  }

  // Encontrar o ponto da fatia da coroa mais próximo do dente adjacente
  const adjacentGeometry = adjacentMesh.geometry;
  const adjacentPositions = adjacentGeometry.attributes.position;
  const tempVertex = new THREE.Vector3();
  
  let closestDistance = Infinity;
  let refinedPoint = null;

  for (const crownVertex of crownVertices) {
    // Calcular distância mínima para o dente adjacente
    let minDistToAdjacent = Infinity;
    
    for (let i = 0; i < adjacentPositions.count; i++) {
      tempVertex.fromBufferAttribute(adjacentPositions, i);
      adjacentMesh.localToWorld(tempVertex);
      
      const dist = crownVertex.distanceTo(tempVertex);
      if (dist < minDistToAdjacent) {
        minDistToAdjacent = dist;
      }
    }
    
    // Manter o ponto da coroa mais próximo do dente adjacente
    if (minDistToAdjacent < closestDistance) {
      closestDistance = minDistToAdjacent;
      refinedPoint = crownVertex.clone();
    }
  }

  return refinedPoint;
}

export function calculatePivotLinePoints__(
  centerOfMass,
  medialPoint,
  distalPoint
) {
  if (!centerOfMass || !medialPoint || !distalPoint) {
    console.warn(
      "Pontos necessários para calcular a linha pivot não fornecidos."
    );
    return [];
  }

  // 1. Ponto central entre medial e distal
  const point2 = medialPoint.clone().add(distalPoint).multiplyScalar(0.5);

  // 2. Direção da linha pivot: da centerOfMass para o ponto2
  const direction = point2.clone().sub(centerOfMass).normalize();

  // 3. Ponto 1: 3 unidades para trás de point2 (na direção oposta)
  const point1 = point2.clone().addScaledVector(direction, -30);

  // 4. Ponto 3: 3 unidades à frente de point2 (na direção original)
  const point3 = point2.clone().addScaledVector(direction, 20);

  return [point1, point2, point3];
}

/**
 * @param {THREE.Scene}     scene
 * @param {THREE.Mesh}      toothMesh        — mesh do dente
 * @param {THREE.Vector3}   centerLocal      — centro de massa em local‑space
 * @param {THREE.Vector3}   medialLocal      — ponto medial em local‑space
 * @param {THREE.Vector3}   distalLocal      — ponto distal em local‑space
 * @param {THREE.Plane|Object} occlusionPlane — ou um THREE.Plane, ou o helper do THREE.PlaneHelper
 * @param {Object}         [opts]            — tamanhos de recuo/avanço
 * @param {number}         [opts.back=2]
 * @param {number}         [opts.forward=30]
 * @returns {THREE.Vector3[]} [pt1, pt2, pt3]
 */
export function calculatePivotLinePoints(
  scene,
  toothMesh,
  centerLocal,
  medialLocal,
  distalLocal,
  occlusionPlane,
  opts = { back: 10, forward: 10 }
) {
  const { back, forward } = opts;

  // 0) sanity check
  if (!centerLocal || !medialLocal || !distalLocal) {
    console.warn("Pontos insuficientes para pivot line.");
    return [];
  }

  // 1) garante matrices corretas
  toothMesh.updateMatrixWorld(true);

  // 2) converte tudo para world‑space
  const comWorld = centerLocal.clone();
  const medialWorld = medialLocal.clone();
  const distalWorld = distalLocal.clone();

  // 3) ponto médio da coroa
  const midWorld = medialWorld.clone().add(distalWorld).multiplyScalar(0.5);

  // 4) extrai o THREE.Plane de occlusionPlane
  let plane;
  if (occlusionPlane instanceof THREE.Plane) {
    plane = occlusionPlane;
  } else if (occlusionPlane.plane instanceof THREE.Plane) {
    // no caso de você ter passado o PlaneHelper
    plane = occlusionPlane.plane;
  } else {
    // busca helper no scene (opcional)
    const helper = scene.children.find((o) => o.plane instanceof THREE.Plane);
    if (!helper) {
      console.warn("Plano de oclusão não encontrado.");
      return [];
    }
    plane = helper.plane;
  }

  // 5) calculo de um ponto arbitrário no plano (plane.constant = – normal⋅p)
  const planeNormal = plane.normal.clone();
  const planePoint = planeNormal.clone().multiplyScalar(-plane.constant);

  // 6) projeta midWorld sobre o plano
  const vecToPlane = midWorld.clone().sub(planePoint);
  const distToPlane = vecToPlane.dot(planeNormal);
  const pivotOrigin = midWorld
    .clone()
    .sub(planeNormal.clone().multiplyScalar(distToPlane));

  // 7) direção pivot: do ponto projetado → centro do dente
  const direction = comWorld.clone().sub(pivotOrigin).normalize();

  // 8) monta os 3 pontos: recuo, origem e avanço
  const pt1 = pivotOrigin.clone().addScaledVector(direction, -back);
  const pt2 = pivotOrigin.clone();
  const pt3 = pivotOrigin.clone().addScaledVector(direction, forward);

  return [pt1, pt2, pt3];
}

export function calculatePivotLinePoints_(
  scene,
  toothMesh,
  centerOfMass,
  medialPoint,
  distalPoint,
  planeData = null
) {
  if (!centerOfMass || !medialPoint || !distalPoint) {
    console.warn(
      "Pontos necessários para calcular a linha pivot não fornecidos."
    );
    return [];
  }

  const normalVector = new THREE.Vector3(
    planeData.normal.x,
    planeData.normal.y,
    planeData.normal.z
  );

  // Criando o plano
  const plane = new THREE.Plane(normalVector, planeData.constant);

  // Supondo que você tenha acesso ao mesh do dente
  const worldCenterOfMass = centerOfMass
    .clone()
    .applyMatrix4(toothMesh.matrixWorld);

  // Pega a normal do plano já normalizada
  const planeNormal = plane.normal.clone(); // já é normalizada

  // Para obter um ponto qualquer sobre o plano:
  const planePoint = planeNormal.clone().multiplyScalar(-plane.constant);

  // 3. Ponto médio entre medial e distal
  const point2 = medialPoint.clone().add(distalPoint).multiplyScalar(0.5);

  var point2Projected = getCrownContactPoint(
    toothMesh,
    planePoint,
    planeNormal
  );

  if (!point2Projected) {
    // fallback se a projeção falhar
    const toPlane = point2.clone().sub(planePoint);
    const distanceToPlane = toPlane.dot(planeNormal);
    point2Projected = point2
      .clone()
      .sub(planeNormal.clone().multiplyScalar(distanceToPlane));
  }

  // 5. Direção da reta da coroa até o centro do dente
  const direction = worldCenterOfMass.clone().sub(point2Projected).normalize();

  // 6. Gera os pontos da reta pivot
  const point1 = point2Projected.clone().addScaledVector(direction, -2); // atrás da coroa
  const point3 = point2Projected.clone().addScaledVector(direction, 30); // em direção ao interior do dente

  return [point1, point2Projected, point3];
}

function getCrownContactPoint(
  toothMesh,
  planePoint,
  planeNormal,
  threshold = 1
) {
  if (!toothMesh.geometry?.attributes?.position) {
    console.warn("Mesh inválida ou sem vértices.");
    return null;
  }

  // Transforma todos os vértices para coordenadas de mundo
  const position = toothMesh.geometry.attributes.position;
  const worldVertices = [];

  for (let i = 0; i < position.count; i++) {
    const vertex = new THREE.Vector3().fromBufferAttribute(position, i);
    vertex.applyMatrix4(toothMesh.matrixWorld);
    worldVertices.push(vertex);
  }

  // Filtra os vértices próximos ao plano de oclusão
  const nearPlaneVertices = worldVertices.filter((vertex) => {
    const toVertex = vertex.clone().sub(planePoint);
    const distance = Math.abs(toVertex.dot(planeNormal));
    return distance < threshold;
  });

  if (nearPlaneVertices.length === 0) {
    console.warn("Nenhum vértice próximo ao plano foi encontrado.");
    return null;
  }

  // Calcula o ponto médio (centro da "coroa projetada")
  const projectedPoint = new THREE.Vector3();
  nearPlaneVertices.forEach((v) => projectedPoint.add(v));
  projectedPoint.divideScalar(nearPlaneVertices.length);

  return projectedPoint;
}

/**
 * Clusteriza as normais das faces de um mesh com geometria não indexada.
 * @param {THREE.Mesh} mesh - O dente individual.
 * @param {number} k - Número de clusters para agrupar as normais.
 * @returns {{
 *   clusterMap: Map<number, number[]>, // clusterId => índices das faces
 *   clusterNormals: THREE.Vector3[],   // normal média de cada cluster
 *   faceNormals: THREE.Vector3[],      // normal de cada face
 * }}
 */
export function clusterizeMeshByNormals(mesh, k = 3) {
  const geom = mesh.geometry.clone();
  geom.computeVertexNormals(); // pode ajudar na visualização posterior

  const position = geom.getAttribute("position");
  const faceNormals = [];
  const normalVectors = [];

  const faceCount = position.count / 3; // cada 3 vértices é uma face

  for (let i = 0; i < faceCount; i++) {
    const a = new THREE.Vector3().fromBufferAttribute(position, i * 3);
    const b = new THREE.Vector3().fromBufferAttribute(position, i * 3 + 1);
    const c = new THREE.Vector3().fromBufferAttribute(position, i * 3 + 2);

    const cb = new THREE.Vector3().subVectors(c, b);
    const ab = new THREE.Vector3().subVectors(a, b);
    const normal = new THREE.Vector3().crossVectors(cb, ab).normalize();

    faceNormals.push(normal);
    normalVectors.push([normal.x, normal.y, normal.z]);
  }

  const result = kmeans(normalVectors, k);
  const clusterMap = new Map();
  const clusterNormals = Array(k)
    .fill(0)
    .map(() => new THREE.Vector3());

  result.clusters.forEach((clusterId, faceIdx) => {
    if (!clusterMap.has(clusterId)) clusterMap.set(clusterId, []);
    clusterMap.get(clusterId).push(faceIdx);
    clusterNormals[clusterId].add(faceNormals[faceIdx]);
  });

  clusterNormals.forEach((n) => n.normalize());

  return {
    clusterMap,
    clusterNormals,
    faceNormals,
  };
}

export function createColoredMeshByCluster(geometry, clusterMap, seed = 0) {
  const newGeometry = geometry.clone();
  console.table(clusterMap);

  // ⚠️ Aplica a matrixWorld se estiver disponível no geometry.userData.meshOriginal
  const originalMesh = geometry.userData?.meshOriginal;
  if (originalMesh && originalMesh.isMesh) {
    const position = geometry.getAttribute("position");
    const newPositions = [];
    const vertex = new THREE.Vector3();
    originalMesh.updateMatrixWorld(true);

    for (let i = 0; i < position.count; i++) {
      vertex.fromBufferAttribute(position, i);
      vertex.applyMatrix4(originalMesh.matrixWorld);
      newPositions.push(vertex.x, vertex.y, vertex.z);
    }

    newGeometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute(newPositions, 3)
    );
  }

  const faceCount = newGeometry.getAttribute("position").count / 3;
  const colors = [];
  const clusterColors = new Map();

  const randomColor = (clusterId) => {
    const r = (Math.sin(clusterId + seed + 1) * 43758.5453) % 1;
    const g = (Math.sin(clusterId + seed + 2) * 43758.5453) % 1;
    const b = (Math.sin(clusterId + seed + 3) * 43758.5453) % 1;
    return new THREE.Color(r, g, b);
  };

  for (const [clusterId, faceIndices] of clusterMap.entries()) {
    const color = randomColor(clusterId);
    clusterColors.set(clusterId, color);

    for (const faceIdx of faceIndices) {
      for (let i = 0; i < 3; i++) {
        colors.push(color.r, color.g, color.b);
      }
    }
  }

  while (colors.length < faceCount * 3 * 3) {
    colors.push(0.8, 0.8, 0.8); // cinza claro
  }

  newGeometry.setAttribute(
    "color",
    new THREE.Float32BufferAttribute(colors, 3)
  );

  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: false,
  });

  return new THREE.Mesh(newGeometry, material);
}
